import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import dayjs, { Dayjs } from "dayjs";

export type ProjectDocumentFolder = {
    id?: string;
    projectId?: string;
    name: string;
    parentFolderId?: string | null;
    orderIndex?: number;
    deleteStatus?: boolean;
    isRoot?: boolean;
    createdAt?: Dayjs;
    updatedAt?: Dayjs;
    deletedAt?: Dayjs;
};

class ProjectDocumentationFolderStore {
    ApiStore!: ApiStore;

    documentFolder: ProjectDocumentFolder | null = null;
    documentsFolders: ProjectDocumentFolder[] = [];
    rootFolder: ProjectDocumentFolder | null = null;

    isLoading = false;

    constructor(apiStore: ApiStore) {
        makeAutoObservable(this);
        this.ApiStore = apiStore;
    }

    get rootFolderId(): string | null {
        return this.rootFolder?.id ? String(this.rootFolder.id) : null;
    }



    /* ----- Helpers ----- */
    private parseDates = (raw: any): ProjectDocumentFolder => ({
        ...raw,
        createdAt: raw?.createdAt ? dayjs(raw.createdAt) : undefined,
        updatedAt: raw?.updatedAt ? dayjs(raw.updatedAt) : undefined,
        deletedAt: raw?.deletedAt ? dayjs(raw.deletedAt) : undefined,
    });

    setLoading = (v: boolean) => (this.isLoading = v);
    setFolders = (folders: ProjectDocumentFolder[]) => (this.documentsFolders = folders);
    setSelectedFolder = (folder: ProjectDocumentFolder | null) => (this.documentFolder = folder);
    setRootFolder = (rootFolder: ProjectDocumentFolder | null) => (this.rootFolder = rootFolder);

    private collectSubtreeIds = (rootId: string) => {
        const ids = new Set<string>([rootId]);
        let added = true;
        while (added) {
            added = false;
            for (const f of this.documentsFolders) {
                const pid = f.parentFolderId ? String(f.parentFolderId) : null;
                if (pid && ids.has(pid) && !ids.has(String(f.id))) {
                    ids.add(String(f.id));
                    added = true;
                }
            }
        }
        return ids;
    };



    /* ----- Public API ----- */
    getByProjectId = async (projectId: string): Promise<ProjectDocumentFolder[]> => {
        this.setLoading(true);
        try {
            const res = await this.ApiStore.ProjectDocumentsFoldersApiClient.getByProjectId(projectId);
            const list = Array.isArray(res) ? res.map(this.parseDates) : [];
            const root =
                list.find(f => f.isRoot) ??
                list.find(f => f.parentFolderId == null) ??
                null;

            this.setRootFolder(root);

            this.setFolders(list);
            return list;
        } catch (error) {
            console.error("Error fetching folders by project ID:", error);
            return [];
        }
        finally {
            this.setLoading(false);
        }
    };

    createNew = async (folder: ProjectDocumentFolder) => {
        this.setLoading(true);
        try {
            const created = await this.ApiStore.ProjectDocumentsFoldersApiClient.add(folder);
            if (!created) return null;

            const parsed = this.parseDates(created);

            this.setFolders([...this.documentsFolders, parsed]);
            this.setSelectedFolder(parsed);
            return parsed;
        } catch (error) {
            console.error("Error creating new folder:", error);
            return null;
        } finally {
            this.setLoading(false);
        }
    };

    updateName = async (folderId: string, newName: string): Promise<ProjectDocumentFolder | null> => {
        const name = (newName ?? "").trim();
        if (!folderId) throw new Error("updateName: folderId is required");
        if (!name) throw new Error("updateName: name cannot be empty");

        this.setLoading(true);
        try {
            const updated = await this.ApiStore.ProjectDocumentsFoldersApiClient.updateName(folderId, name);

            const idx = this.documentsFolders.findIndex(f => String(f.id) === String(folderId));
            if (idx === -1) {
                const fallback: ProjectDocumentFolder = {
                    id: folderId,
                    name,
                    updatedAt: dayjs(),
                };

                let parsedFromResponse: Partial<ProjectDocumentFolder> = {};
                if (updated && typeof updated === "object") {
                    const maybeValue = (updated as any).value ?? updated;
                    if (maybeValue && typeof maybeValue === "object") {
                        parsedFromResponse = this.parseDates(maybeValue);
                    }
                }

                this.setFolders([...this.documentsFolders, { ...fallback, ...parsedFromResponse, name }]);
                return { ...fallback, ...parsedFromResponse, name };
            }

            const next = this.documentsFolders.slice();

            let parsedFromResponse: Partial<ProjectDocumentFolder> = {};
            if (updated && typeof updated === "object") {
                const maybeValue = (updated as any).value ?? updated;
                if (maybeValue && typeof maybeValue === "object") {
                    parsedFromResponse = this.parseDates(maybeValue);
                }
            }

            next[idx] = {
                ...next[idx],
                ...parsedFromResponse,
                name,
                updatedAt: dayjs(),
            };

            this.setFolders(next);

            if (this.documentFolder && String(this.documentFolder.id) === String(folderId)) {
                this.setSelectedFolder(next[idx]);
            }

            return next[idx];
        } catch (error) {
            console.error("Error updating folder name:", error);
            return null;
        } finally {
            this.setLoading(false);
        }
    };

    updatePosition = async (folderId: string, newParentFolderId: string | null, newOrderIndex: number): Promise<ProjectDocumentFolder | null> => {
        // let's find the folder
        const idx = this.documentsFolders.findIndex(f => String(f.id) === String(folderId));
        if (idx === -1) {
            console.error("updatePosition: folder not found in store");
            return null;
        }

        // clone of the previous state for possible rollback
        const prev = this.documentsFolders.slice();

        // helpers
        const keyOf = (pid: string | number | null | undefined) => (pid == null ? "__root__" : String(pid));

        const reindex = (arr: ProjectDocumentFolder[]) => {
            arr.forEach((f, i) => (f.orderIndex = i + 1));
        };

        const patchManyInto = (target: ProjectDocumentFolder[], changed: ProjectDocumentFolder[]) => {
            const map = new Map(changed.map(f => [String(f.id), f]));
            for (let i = 0; i < target.length; i++) {
                const k = String(target[i].id);
                if (map.has(k)) target[i] = { ...target[i], ...map.get(k)! };
            }
        };

        // current folder/parent
        const moving = { ...this.documentsFolders[idx] };
        const oldParentId = moving.parentFolderId ?? null;
        const newParentId = newParentFolderId ?? null;

        // normalize the target index (min 1)
        let targetIndex = Number.isFinite(newOrderIndex) ? Math.max(1, Math.floor(newOrderIndex)) : 1;

        // we will prepare a working copy of the list
        const next = this.documentsFolders.map(f => ({ ...f }));

        // we select lists of "neighbors" by old/new parent
        const oldSiblings = next
            .filter(f => keyOf(f.parentFolderId) === keyOf(oldParentId) && String(f.id) !== String(folderId))
            .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

        const newSiblings = next
            .filter(f => keyOf(f.parentFolderId) === keyOf(newParentId) && String(f.id) !== String(folderId))
            .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

        // permutation within one parent
        if (keyOf(oldParentId) === keyOf(newParentId)) {
            const list = oldSiblings; // identical set
            // insert the element in the desired place
            const clampIndex = Math.min(Math.max(targetIndex, 1), list.length + 1) - 1;
            const inserted: ProjectDocumentFolder[] = [
                ...list.slice(0, clampIndex),
                { ...moving, parentFolderId: newParentId, orderIndex: clampIndex + 1 },
                ...list.slice(clampIndex),
            ];
            reindex(inserted);

            // updating next
            patchManyInto(next, inserted);

            // we update the element itself
            const selfIdx = next.findIndex(f => String(f.id) === String(folderId));
            if (selfIdx !== -1) next[selfIdx] = inserted.find(f => String(f.id) === String(folderId))!;

        } else {
            // transfer between different parents

            // 1) reindex old neighbors (without moving)
            reindex(oldSiblings);

            // 2) we insert into new neighbors
            const clampIndex = Math.min(Math.max(targetIndex, 1), newSiblings.length + 1) - 1;
            const newList: ProjectDocumentFolder[] = [
                ...newSiblings.slice(0, clampIndex),
                { ...moving, parentFolderId: newParentId, orderIndex: clampIndex + 1 },
                ...newSiblings.slice(clampIndex),
            ];
            reindex(newList);

            // 3) collect all changed elements
            patchManyInto(next, oldSiblings);
            patchManyInto(next, newList);

            // 4) update the element itself (to make sure it has a new parent/index)
            const selfIdx = next.findIndex(f => String(f.id) === String(folderId));
            if (selfIdx !== -1) next[selfIdx] = newList.find(f => String(f.id) === String(folderId))!;
        }

        // optimistically updating the page
        this.setFolders(next);
        if (this.documentFolder && String(this.documentFolder.id) === String(folderId)) {
            const fresh = next.find(f => String(f.id) === String(folderId)) || null;
            this.setSelectedFolder(fresh);
        }

        // network request
        this.setLoading(true);
        try {
            const resp = await this.ApiStore.ProjectDocumentsFoldersApiClient.updatePosition(
                String(folderId),
                newParentId == null ? null as any : String(newParentId),
                String((next.find(f => String(f.id) === String(folderId))?.orderIndex ?? 1))
            );

            // if the backend returned the updated model - we will paste the fresh dates/fields
            if (resp && typeof resp === "object") {
                const maybeValue = (resp as any).value ?? resp;
                if (maybeValue && typeof maybeValue === "object") {
                    const parsed = this.parseDates(maybeValue);
                    const fixIdx = this.documentsFolders.findIndex(f => String(f.id) === String(folderId));
                    if (fixIdx !== -1) {
                        const patched = { ...this.documentsFolders[fixIdx], ...parsed };
                        const after = this.documentsFolders.slice();
                        after[fixIdx] = patched;
                        this.setFolders(after);
                        if (this.documentFolder && String(this.documentFolder.id) === String(folderId)) {
                            this.setSelectedFolder(patched);
                        }
                    }
                }
            }

            return this.documentsFolders.find(f => String(f.id) === String(folderId)) || null;
        } catch (e) {
            console.error("updatePosition failed, reverting", e);
            // rollback
            this.setFolders(prev);
            if (this.documentFolder && String(this.documentFolder.id) === String(folderId)) {
                const oldSel = prev.find(f => String(f.id) === String(folderId)) || null;
                this.setSelectedFolder(oldSel);
            }
            return null;
        } finally {
            this.setLoading(false);
        }
    };

    deleteById = async (folderId: string) => {
        const subtreeIds = this.collectSubtreeIds(folderId);
        const prev = this.documentsFolders.slice();

        this.setFolders(prev.filter(f => !subtreeIds.has(String(f.id))));
        if (this.documentFolder && subtreeIds.has(String(this.documentFolder.id))) {
            this.setSelectedFolder(null);
        }

        try {
            this.setLoading(true);
            const res = await this.ApiStore.ProjectDocumentsFoldersApiClient.deleteById(folderId);
            if (!res) {
                this.setFolders(prev);
                return false;
            }
            return true;
        } catch (e) {
            console.error("Error deleting folder:", e);
            this.setFolders(prev);
            return false;
        } finally {
            this.setLoading(false);
        }
    };
}

export default ProjectDocumentationFolderStore;